import {useState} from 'react';
import {AnimatePresence, motion} from 'framer-motion';
import PageHero from '../components/PageHero';
import PatternPreview, {patternNames} from '../components/PatternPreview';
import CtaBand from '../components/CtaBand';
import Reveal from '../components/Reveal';
import {materials} from '../data/site';

const materialPhotos = {
  'Old Chicago': {
    src: 'https://images.pexels.com/photos/220182/pexels-photo-220182.jpeg?auto=compress&amp;cs=tinysrgb&amp;w=1400',
    alt: 'Weathered brick with varied, aged tones',
    caption: 'Weathered brick inspiration'
  },
  'Classic Brick': {
    src: 'https://images.pexels.com/photos/207142/pexels-photo-207142.jpeg?auto=compress&amp;cs=tinysrgb&amp;w=1400',
    alt: 'Traditional red brick texture',
    caption: 'Red brick inspiration'
  },
  Travertine: {
    src: 'https://images.unsplash.com/photo-1572331165267-854da2b10ccc?auto=format&amp;fit=crop&amp;w=1400&amp;q=85',
    alt: 'Natural stone around an outdoor swimming pool',
    caption: 'Poolside stone inspiration'
  },
  Marble: {
    src: 'https://images.unsplash.com/photo-1576013551627-0cc20b96c2a7?auto=format&amp;fit=crop&amp;w=1400&amp;q=85',
    alt: 'Light-toned stone in an outdoor pool setting',
    caption: 'Light stone inspiration'
  }
};

function Visualizer() {
  const [mi, setMi] = useState(0);
  const [pattern, setPattern] = useState(patternNames[0]);
  const m = materials[mi];
  const photo = materialPhotos[m.name];

  return (
    <section className="py-24 md:py-32">
      <div className="wrap grid gap-12 lg:grid-cols-[1.15fr_0.85fr]">
        <Reveal>
          <div className="relative aspect-[4/3] overflow-hidden bg-ink shadow-[0_30px_80px_rgba(20,17,15,0.2)]">
            <AnimatePresence mode="wait">
              <motion.img
                key={m.name}
                src={photo.src.replaceAll('&amp;', '&')}
                alt={photo.alt}
                className="absolute inset-0 h-full w-full object-cover"
                initial={{opacity: 0, scale: 1.06}}
                animate={{opacity: 1, scale: 1}}
                exit={{opacity: 0}}
                transition={{duration: 0.45}}
              />
            </AnimatePresence>
            <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-ink/90 to-transparent p-6 text-white">
              <p className="text-[10px] font-bold uppercase tracking-widest text-clay">{photo.caption}</p>
              <p className="h-display text-4xl">{m.name}</p>
            </div>
          </div>
          <p className="mt-3 text-xs leading-5 text-stone">
            Photos are material inspiration, not product samples or completed US Paver Co. installations. Confirm color and texture with physical samples.
          </p>
        </Reveal>
        <Reveal delay={0.1}>
          <p className="eyebrow">Paver visualizer</p>
          <h2 className="h-display mt-4 text-5xl md:text-6xl">Find your <em className="text-brick">perfect finish.</em></h2>
          <div className="mt-8 grid grid-cols-2 gap-3">
            {materials.map((item, i) => (
              <button
                key={item.name}
                type="button"
                aria-pressed={mi === i}
                onClick={() => setMi(i)}
                className={`flex items-center gap-3 border p-2 text-left transition ${mi === i ? 'border-ink bg-white shadow-lg' : 'border-ink/15 hover:border-ink'}`}
              >
                <img
                  src={materialPhotos[item.name].src.replaceAll('&amp;', '&')}
                  alt=""
                  loading="lazy"
                  className="h-12 w-12 shrink-0 object-cover"
                />
                <span className="text-sm font-bold">{item.name}</span>
              </button>
            ))}
          </div>
          <p className="mt-8 text-xs font-bold uppercase tracking-widest">Explore laying patterns</p>
          <div className="mt-3 flex flex-wrap gap-2">
            {patternNames.map((p) => (
              <button
                key={p}
                type="button"
                aria-pressed={pattern === p}
                onClick={() => setPattern(p)}
                className={`px-4 py-3 text-xs font-bold transition ${pattern === p ? 'bg-ink text-white' : 'border border-ink/20 hover:bg-ink hover:text-white'}`}
              >
                {p}
              </button>
            ))}
          </div>
          <div className="mt-5 flex items-center gap-4 bg-white p-3">
            <div className="h-20 w-28 shrink-0 overflow-hidden">
              <PatternPreview material={m} pattern={pattern} />
            </div>
            <p className="text-xs leading-5 text-stone">
              <strong className="block text-ink">{pattern}</strong>
              Illustrative laying-pattern diagram; the photograph above does not change with pattern selection.
            </p>
          </div>
          <div className="mt-8 border-t border-ink/15 pt-6">
            <p className="h-display text-3xl text-brick">{m.mood}</p>
            <p className="mt-3 text-sm leading-7 text-stone">{m.text}</p>
            <p className="mt-4 text-xs font-bold uppercase tracking-widest">
              Best for: <span className="font-medium normal-case tracking-normal text-stone">{m.best}</span>
            </p>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

function Materials() {
  return (
    <>
      <PageHero
        eyebrow="Paver styles"
        title="Materials with"
        accent="character."
        text="Brick, travertine and marble, each with its own personality. Explore them side by side."
        image="https://images.unsplash.com/photo-1600607687920-4e2a09cf159d?auto=format&amp;fit=crop&amp;w=2000&amp;q=85"
      />
      <Visualizer />
      <section className="bg-sand py-24">
        <div className="wrap grid gap-px bg-ink/10 md:grid-cols-2">
          {materials.map((m, i) => (
            <Reveal key={m.name} delay={(i % 2) * 0.08} className="bg-bone p-10">
              <p className="eyebrow">0{i + 1}</p>
              <h3 className="h-display mt-3 text-5xl">{m.name}</h3>
              <p className="mt-4 text-sm leading-7 text-stone">{m.text}</p>
              <p className="mt-5 text-xs font-bold uppercase tracking-widest text-brick">{m.best}</p>
            </Reveal>
          ))}
        </div>
      </section>
      <CtaBand title="Want to see samples in your own light?" />
    </>
  );
}

export default Materials;
